import React, { useMemo } from 'react';
import { VehiculeConfig, Service, PersonnelConfig, Competence, FonctionArmement } from '../../../../../../shared/types/config';
import VehiculeGradePriority from './VehiculeGradePriority';

interface VehiculeModalProps {
  editingVehicule: VehiculeConfig | null;
  vehiculeForm: Omit<VehiculeConfig, 'id'>;
  setVehiculeForm: React.Dispatch<React.SetStateAction<Omit<VehiculeConfig, 'id'>>>;
  services: Service[];
  /** Grades (config_personnel) du même service : ordre de priorité sur l’engin */
  personnelConfigs: PersonnelConfig[];
  competences: Competence[];
  fonctionsArmement: FonctionArmement[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const VehiculeModal: React.FC<VehiculeModalProps> = ({
  editingVehicule,
  vehiculeForm,
  setVehiculeForm,
  services,
  personnelConfigs,
  competences,
  fonctionsArmement,
  onClose,
  onSave,
}) => {
  const primaryFunctionCompetences = useMemo(() => {
    const f = fonctionsArmement.find((x) => x.id === vehiculeForm.primary_armament_function_id);
    const ids = (f?.id_competences || [])
      .map((item: any) => parseInt(String(item?.id ?? 0), 10))
      .filter((id) => Number.isInteger(id) && id > 0);
    return Array.from(new Set(ids))
      .map((id) => competences.find((c) => c.id === id))
      .filter((c): c is Competence => Boolean(c));
  }, [fonctionsArmement, vehiculeForm.primary_armament_function_id, competences]);

  const secondaryFunctionIds = vehiculeForm.secondary_armament_function_ids ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-2xl font-bold text-white">
            {editingVehicule ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom *</label>
              <input
                type="text"
                value={vehiculeForm.nom}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, nom: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <input
                type="text"
                value={vehiculeForm.type}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, type: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Service *</label>
              <select
                value={Number(vehiculeForm.id_service) > 0 ? Number(vehiculeForm.id_service) : 0}
                onChange={(e) => {
                  const sid = parseInt(e.target.value, 10) || 0;
                  setVehiculeForm({ ...vehiculeForm, id_service: sid });
                }}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.nom}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Niveaux *</label>
              <input
                type="number"
                value={vehiculeForm.niveaux}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, niveaux: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Prix *</label>
              <input
                type="number"
                value={vehiculeForm.prix}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, prix: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Kilométrage *</label>
              <input
                type="number"
                value={vehiculeForm.kilometrage}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, kilometrage: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fonction départ *</label>
              <input
                type="text"
                value={vehiculeForm.fonction_depart}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, fonction_depart: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité personnels min *</label>
              <input
                type="number"
                value={vehiculeForm.capacite_personnels_min}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_personnels_min: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité personnels max *</label>
              <input
                type="number"
                value={vehiculeForm.capacite_personnels_max}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_personnels_max: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité eau (L)</label>
              <input
                type="number"
                value={vehiculeForm.capacite_eau}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_eau: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité émulseur (L)</label>
              <input
                type="number"
                value={vehiculeForm.capacite_emulseur}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_emulseur: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Fonction d'armement principale *</label>
              <select
                value={vehiculeForm.primary_armament_function_id ?? 0}
                onChange={(e) => {
                  const nextPrimaryId = parseInt(e.target.value, 10) || 0;
                  setVehiculeForm({
                    ...vehiculeForm,
                    primary_armament_function_id: nextPrimaryId > 0 ? nextPrimaryId : null,
                    fonction_depart:
                      nextPrimaryId > 0
                        ? (fonctionsArmement.find((f) => f.id === nextPrimaryId)?.nom || vehiculeForm.fonction_depart)
                        : vehiculeForm.fonction_depart,
                    secondary_armament_function_ids: secondaryFunctionIds.filter((id) => id !== nextPrimaryId),
                    id_fonctions_armement: secondaryFunctionIds.filter((id) => id !== nextPrimaryId),
                  });
                }}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Sélectionner une fonction principale</option>
                {fonctionsArmement.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Fonctions d'armement secondaires
                {secondaryFunctionIds.length > 0 && (
                  <span className="ml-2 text-xs text-blue-400">
                    ({secondaryFunctionIds.length} sélectionnée{secondaryFunctionIds.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-h-44 overflow-y-auto">
                {fonctionsArmement.length === 0 ? (
                  <p className="text-gray-400 text-sm">Aucune fonction disponible.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {fonctionsArmement
                      .filter((f) => f.id !== vehiculeForm.primary_armament_function_id)
                      .map((f) => {
                        const checked = secondaryFunctionIds.includes(f.id);
                        return (
                          <label key={f.id} className="flex items-start gap-2 p-2 rounded hover:bg-gray-700/50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...secondaryFunctionIds, f.id]
                                  : secondaryFunctionIds.filter((id) => id !== f.id);
                                const uniq = Array.from(new Set(next));
                                setVehiculeForm({
                                  ...vehiculeForm,
                                  secondary_armament_function_ids: uniq,
                                  id_fonctions_armement: uniq,
                                });
                              }}
                              className="w-4 h-4 mt-0.5 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-300">{f.nom}</span>
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Compétences d&apos;armement (définies par la fonction principale)
                {primaryFunctionCompetences.length > 0 && (
                  <span className="ml-2 text-xs text-blue-400">
                    ({primaryFunctionCompetences.length})
                  </span>
                )}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Les compétences ne se configurent plus directement sur le véhicule. Elles sont héritées de la fonction d&apos;armement principale.
              </p>
              {!vehiculeForm.primary_armament_function_id ? (
                <p className="text-gray-400 text-sm">Choisissez d&apos;abord une fonction principale.</p>
              ) : (
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-h-56 overflow-y-auto">
                    {primaryFunctionCompetences.length === 0 ? (
                      <p className="text-gray-400 text-sm">
                        Cette fonction principale n&apos;a aucune compétence configurée.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {primaryFunctionCompetences.map((c) => (
                          <div key={c.id} className="rounded bg-gray-700/40 px-2 py-1.5 text-sm text-gray-300">
                            {c.nom}
                            {c.is_specialite && (
                              <span className="ml-2 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">Spé</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
              )}
            </div>

            <VehiculeGradePriority
              vehiculeForm={vehiculeForm}
              setVehiculeForm={setVehiculeForm}
              personnelConfigs={personnelConfigs}
            />


            <div className="col-span-2 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Capacité victimes UR</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_victimes_ur ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_victimes_ur: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Capacité victimes UA</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_victimes_ua ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_victimes_ua: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Capacité impliqués</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_victimes_impliquees ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_victimes_impliquees: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Capacité hostiles</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_hostiles ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_hostiles: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Capacité véhicules accidentés</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_vehicules_accidentes ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_vehicules_accidentes: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Capacité animaux</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_animaux ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_animaux: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.specialiste}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, specialiste: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Véhicule spécialiste (indicateur / règles métier côté jeu)
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_remorque}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_remorque: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable remorque
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_lot}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_lot: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable lot
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_cellule}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_cellule: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable cellule
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={Boolean(vehiculeForm.capable_escorte_medicale)}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_escorte_medicale: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable escorte médicale
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={Boolean(vehiculeForm.capable_escorte_fdo)}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_escorte_fdo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable escorte FDO
              </label>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            {editingVehicule ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehiculeModal;
